import React, { useContext, useState } from 'react';
import { graphql } from 'gatsby';
import { HelmetDatoCms } from 'gatsby-source-datocms';
import Layout from '../components/Layout';
import { Container, Grid } from '../components/ui';
import Breadcrumbs from '../components/Breadcrumbs';
import ProductGallery from '../components/ProductGallery';
import ProductDetails from '../components/ProductDetails';
import Accordion from '../components/Accordion';
import GrowsWellTogether from '../components/GrowsWellTogether';
import CompanyReviews from '../components/CompanyReviews';
import RelatedProducts from '../components/RelatedProducts';
import IconCtas from '../components/IconCtas';
import { buildUrl } from '../utils';
import { StoreContext } from '../context/StoreContext';

const ProductPageTemplate = ({
  data: {
    shopifyProduct,
    allShopifyProductVariant: { variants },
    googleReviews,
    datoCmsProduct,
    crossSellProducts,
    growsWellTogether,
    potUpsells: potUpsellsUnordered,
    relatedProducts,
    relatedProductsFallback,
    datoCmsProductSitewide: { accordionItems },
    datoCmsHome: { companyReviewsHeading },
    datoCmsSite: {
      globalSeo: { titleSuffix },
    },
  },
  pageContext: { potUpsells: potUpsellsOrder },
}) => {
  const {
    storefrontId,
    legacyResourceId,
    title,
    handle,
    images,
    options,
    descriptionHtml,
    seo,
  } = shopifyProduct;
  const { currency } = useContext(StoreContext);

  const [selectedVariant, setSelectedVariant] = useState(variants[0]);

  const potUpsellsOrdered = potUpsellsUnordered.nodes.sort(
    (a, b) =>
      potUpsellsOrder.indexOf(a.handle) - potUpsellsOrder.indexOf(b.handle)
  );

  const potUpsells = potUpsellsOrdered.map(defaultPotUpsell => {
    const potUpsell = datoCmsProduct.potUpsellGroup.potUpsells.find(
      ({ shopifyProduct }) => shopifyProduct === defaultPotUpsell.handle
    );

    return {
      ...defaultPotUpsell,
      ...potUpsell,
    };
  });

  const productUrl =
    process.env.GATSBY_SITE_URL + buildUrl(handle, shopifyProduct);

  const productDescription =
    datoCmsProduct?.shortDescription || seo?.description;

  const createOffer = variant => {
    const { price } = variant.presentmentPrices
      ? variant.presentmentPrices.find(
          ({ price }) => price && price.currencyCode === currency
        ) || {}
      : variant;

    return {
      '@type': 'Offer',
      url: productUrl,
      ...(price?.amount && {
        price: price.amount,
        priceCurrency: price.currencyCode,
      }),
      availability: variant.availableForSale
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
      ...(variant.sku && {
        sku: variant.sku,
      }),
    };
  };

  const getVariantImage = variant => {
    const variantImage = variant.image?.[0]?.preview?.image?.src;
    const mainProductImage = images?.[0]?.preview?.image?.src;

    return variantImage || mainProductImage;
  };

  const createProduct = variant => {
    const variantImage = getVariantImage(variant);

    return {
      '@type': 'Product',
      name:
        variant.title && variant.title !== 'Default Title'
          ? `${title} - ${variant.title}`
          : title,
      ...(productDescription && {
        description: productDescription,
      }),
      url: productUrl,
      ...(variant.sku && {
        sku: variant.sku,
        productID: variant.sku,
      }),
      ...(variantImage && {
        image: variantImage,
      }),
      offers: createOffer(variant),
    };
  };

  const products = variants.map(variant => ({
    ...createProduct(variant),
    ...(variants.length > 1 && {
      '@id': `${productUrl}#variant-${variant.legacyResourceId}`,
      isVariantOf: {
        '@id': `${productUrl}#product`,
      },
    }),
  }));

  const schema =
    variants.length === 1
      ? {
          '@context': 'https://schema.org',
          '@graph': products,
        }
      : {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'ProductGroup',
              '@id': `${productUrl}#product`,
              name: title,
              ...(productDescription && {
                description: productDescription,
              }),
              url: productUrl,
              productGroupID: String(legacyResourceId),
              hasVariant: products.map(product => ({
                '@id': product['@id'],
              })),
            },
            ...products,
          ],
        };

  return (
    <Layout title={title} description={seo.description}>
      <HelmetDatoCms>
        <script type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
        <title>{`${seo.title || title}${titleSuffix}`}</title>
        {seo.description && (
          <meta name="description" content={seo.description} />
        )}
      </HelmetDatoCms>
      <main>
        <Container>
          <Breadcrumbs
            breadcrumbs={[{ slug: 'shop', text: 'Shop' }]}
            currentPageTitle={title}
          />
          <Grid>
            <ProductGallery
              images={images}
              title={title}
              selectedVariantImage={selectedVariant.image}
            />
            <ProductDetails
              storefrontId={storefrontId}
              legacyResourceId={legacyResourceId}
              title={title}
              handle={handle}
              googleReviews={googleReviews}
              shortDescription={
                datoCmsProduct && datoCmsProduct.shortDescription
              }
              careIcons={datoCmsProduct && datoCmsProduct.careIcons}
              options={options}
              variants={variants}
              selectedVariant={selectedVariant}
              setSelectedVariant={setSelectedVariant}
              potUpsells={potUpsells}
              crossSellProducts={crossSellProducts.nodes}
            />
          </Grid>
        </Container>
        <Accordion
          heading="Useful Information"
          hideHeading={true}
          items={[
            { heading: 'Plant Bio', content: descriptionHtml },
            ...accordionItems,
          ]}
          isProductPage={true}
        />
        <GrowsWellTogether items={growsWellTogether.nodes} />
        <CompanyReviews heading={companyReviewsHeading} />
        <RelatedProducts
          items={
            (relatedProducts.nodes.length > 0 && relatedProducts.nodes) ||
            (relatedProductsFallback.nodes.length > 0 &&
              relatedProductsFallback.nodes)
          }
        />
        <IconCtas isProductPage={true} />
      </main>
    </Layout>
  );
};

export const ProductPageTemplateQuery = graphql`
  query ProductPageTemplateQuery(
    $id: String!
    $handle: String!
    $crossSellProducts: [String]!
    $growsWellTogether: [String]!
    $potUpsells: [String]
    $relatedProducts: [String]
    $firstCollectionId: String
  ) {
    shopifyProduct(id: { eq: $id }) {
      internal {
        type
      }
      storefrontId
      legacyResourceId
      title
      handle
      images: media {
        id
        preview {
          image {
            src
            gatsbyImageData(width: 560, height: 680)
            altText
          }
        }
      }
      options {
        name
      }
      descriptionHtml
      seo {
        title
        description
      }
    }
    allShopifyProductVariant(
      filter: { _product: { eq: $id } }
      sort: { fields: availableForSale, order: DESC }
    ) {
      variants: nodes {
        id
        storefrontId
        legacyResourceId
        title
        sku
        availableForSale
        presentmentPrices {
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
        }
        image: media {
          id
          preview {
            image {
              src
              gatsbyImageData(width: 560, height: 680)
              altText
            }
          }
        }
        metafields {
          key
          value
        }
      }
    }
    googleReviews: googleReview {
      averageRating
      totalReviewsCount
    }
    datoCmsProduct(shopifyProduct: { eq: $handle }) {
      shortDescription
      careIcons {
        url
        alt
      }
      potUpsellGroup {
        potUpsells {
          name
          description
          shopifyProduct
        }
      }
      ...LinkFragment
    }
    crossSellProducts: allShopifyProduct(
      filter: { handle: { in: $crossSellProducts } }
    ) {
      nodes {
        title
        featuredImage {
          gatsbyImageData(width: 180, height: 180)
          altText
        }
        variants {
          storefrontId
          presentmentPrices {
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
    growsWellTogether: allShopifyProduct(
      filter: { handle: { in: $growsWellTogether } }
    ) {
      nodes {
        ...ProductCardFragment
      }
    }
    potUpsells: allShopifyProduct(filter: { handle: { in: $potUpsells } }) {
      nodes {
        handle
        variants {
          storefrontId
          presentmentPrices {
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
          availableForSale
          selectedOptions {
            name
            value
          }
          metafields {
            key
            value
          }
        }
      }
    }
    relatedProducts: allShopifyProduct(
      filter: { handle: { in: $relatedProducts } }
    ) {
      nodes {
        ...ProductCardFragment
      }
    }
    relatedProductsFallback: allShopifyProduct(
      filter: {
        id: { ne: $id }
        collections: { elemMatch: { id: { eq: $firstCollectionId } } }
        totalInventory: { gt: 0 }
      }
      limit: 8
    ) {
      nodes {
        ...ProductCardFragment
      }
    }
    datoCmsProductSitewide {
      accordionItems {
        id
        heading
        content
      }
    }
    datoCmsHome {
      companyReviewsHeading
    }
    datoCmsSite {
      globalSeo {
        titleSuffix
      }
    }
  }
`;

export default ProductPageTemplate;
